import React, { useEffect, useState } from "react";
import { Edit3, ImagePlus, Loader2, Plus, Save } from "lucide-react";
import {
  savePhoenixSpace,
  seedDefaultPhoenixSpaces,
  subscribePhoenixSpaces,
  uploadPhoenixSpaceImage,
} from "../service";
import { AdminPageHeader, PhoenixAdminNav } from "./AdminShared";

const emptySpace = {
  id: "",
  name: "",
  slug: "",
  description: "",
  location: "",
  capacity: "",
  hourlyRate: "",
  rateLabel: "",
  suitableActivitiesText: "",
  facilitiesText: "",
  accessibility: "",
  images: [],
  active: true,
  displayOrder: 10,
};

function toEditSpace(space) {
  return {
    ...emptySpace,
    ...space,
    suitableActivitiesText: (space.suitableActivities || []).join("\n"),
    facilitiesText: (space.facilities || []).join("\n"),
  };
}

function listFromText(value) {
  return String(value || "").split(/\n|,/).map((item) => item.trim()).filter(Boolean);
}

export default function PhoenixSpacesAdmin() {
  const [spaces, setSpaces] = useState([]);
  const [editing, setEditing] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => subscribePhoenixSpaces(setSpaces, () => setError("Spaces could not be loaded.")), []);

  const update = (key, value) => setEditing((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!editing.name.trim() || !editing.slug.trim()) { setError("A name and slug are required."); return; }
    setSaving(true); setError(""); setMessage("");
    try {
      let images = editing.images || [];
      if (imageFile) images = [await uploadPhoenixSpaceImage(editing.id || editing.slug, imageFile)];
      await savePhoenixSpace({
        ...editing,
        id: editing.id || editing.slug,
        suitableActivities: listFromText(editing.suitableActivitiesText),
        facilities: listFromText(editing.facilitiesText),
        images,
      });
      setEditing(null); setImageFile(null); setMessage("Space details saved.");
    } catch (saveError) { setError(saveError?.message || "The space could not be saved."); }
    finally { setSaving(false); }
  };

  const seed = async () => {
    if (!window.confirm("Add or refresh the four default Phoenix Hub spaces? Existing custom fields with the same IDs will be overwritten.")) return;
    setSaving(true); setError("");
    try { await seedDefaultPhoenixSpaces(); setMessage("Default Phoenix Hub spaces added."); }
    catch (seedError) { setError(seedError?.message || "Default spaces could not be added."); }
    finally { setSaving(false); }
  };

  return (
    <div className="phx-shell phx-admin-shell">
      <PhoenixAdminNav />
      <AdminPageHeader title="Manage Phoenix Hub spaces"><p>Edit public descriptions, rates, capacities, facilities, images and display order without changing the site code.</p></AdminPageHeader>
      {error ? <div className="phx-submit-error" role="alert">{error}</div> : null}{message ? <div className="phx-availability-available" role="status">{message}</div> : null}
      <div className="phx-admin-actions"><button type="button" className="phx-button" onClick={() => setEditing({ ...emptySpace })}><Plus aria-hidden="true" /> Add a space</button><button type="button" className="phx-button phx-button-secondary" onClick={seed} disabled={saving}>Add or refresh defaults</button></div>

      {editing ? <section className="phx-admin-card"><h2>{editing.id ? `Edit ${editing.name}` : "Add a space"}</h2><div className="phx-admin-form"><label>Name<input value={editing.name} onChange={(e) => update("name", e.target.value)} /></label><label>Slug<input value={editing.slug} onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))} /></label><label className="phx-full">Description<textarea rows="4" value={editing.description} onChange={(e) => update("description", e.target.value)} /></label><label>Floor or location<input value={editing.location} onChange={(e) => update("location", e.target.value)} /></label><label>Capacity<input type="number" min="0" value={editing.capacity ?? ""} onChange={(e) => update("capacity", e.target.value)} /></label><label>Indicative hourly rate (£)<input type="number" min="0" step="0.01" value={editing.hourlyRate ?? ""} onChange={(e) => update("hourlyRate", e.target.value)} /></label><label>Rate label<input value={editing.rateLabel} onChange={(e) => update("rateLabel", e.target.value)} placeholder="From £35 per hour" /></label><label className="phx-full">Suitable activities (one per line)<textarea rows="6" value={editing.suitableActivitiesText} onChange={(e) => update("suitableActivitiesText", e.target.value)} /></label><label className="phx-full">Facilities (one per line)<textarea rows="5" value={editing.facilitiesText} onChange={(e) => update("facilitiesText", e.target.value)} /></label><label className="phx-full">Accessibility information<textarea rows="4" value={editing.accessibility} onChange={(e) => update("accessibility", e.target.value)} /></label><label>Display order<input type="number" min="0" value={editing.displayOrder} onChange={(e) => update("displayOrder", e.target.value)} /></label><label className="phx-admin-checkbox"><span>Publicly active</span><input type="checkbox" checked={editing.active} onChange={(e) => update("active", e.target.checked)} /></label><label className="phx-full">Upload or replace the primary JPG, PNG or WebP image<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setImageFile(e.target.files?.[0] || null)} /></label></div><div className="phx-admin-actions"><button type="button" className="phx-button" onClick={save} disabled={saving}>{saving ? <Loader2 className="phx-spin" aria-hidden="true" /> : imageFile ? <ImagePlus aria-hidden="true" /> : <Save aria-hidden="true" />} Save space</button><button type="button" className="phx-button phx-button-secondary" onClick={() => { setEditing(null); setImageFile(null); }}>Cancel</button></div></section> : null}

      <section className="phx-admin-space-list">{spaces.map((space) => <article key={space.id}><p className="phx-kicker">{space.active ? "Active" : "Inactive"} · order {space.displayOrder}</p><h2>{space.name}</h2><p>{space.description}</p><strong>{space.rateLabel} {space.capacity ? `· capacity ${space.capacity}` : ""}</strong><div className="phx-admin-actions"><button type="button" className="phx-button phx-button-secondary" onClick={() => setEditing(toEditSpace(space))}><Edit3 aria-hidden="true" /> Edit</button><button type="button" className="phx-button phx-button-secondary" onClick={async () => { await savePhoenixSpace({ ...space, active: !space.active }); }} >Mark {space.active ? "inactive" : "active"}</button></div></article>)}</section>
    </div>
  );
}
